import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  ArrowUpRight,
  Star,
  Quote,
  X,
  Layers,
  Filter,
  TrendingUp,
} from "lucide-react";
import Header from "../../components/layout/Header";
import Footer from "../../components/layout/Footer";

const fadeUp = {
  hidden: { opacity: 0, y: 30, filter: "blur(6px)" },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    filter: "blur(0px)",
    transition: { duration: 0.7, delay: i * 0.1, ease: [0.16, 1, 0.3, 1] },
  }),
};
const stagger = { visible: { transition: { staggerChildren: 0.08 } } };

/* ─── PORTFOLIO DATA ─── */
const categories = [
  "All",
  "Branding",
  "Marketing",
  "Development",
  "AI Automation",
  "UI/UX Design",
  "Content",
];

const handleImageFallback = (e) => {
  e.currentTarget.onerror = null;
  e.currentTarget.src = "/assets/images/no_image.png";
};

const projects = [
  {
    title: "Luxury Resort Rebrand",
    category: "Branding",
    client: "Paradise Resorts",
    desc: "Complete brand overhaul: logo, visual identity, marketing collateral, and website. Massive increase in premium bookings.",
    image:
      "https://images.unsplash.com/photo-1571896349842-33c89424de2d?auto=format&fit=crop&w=800&q=80",
    stat: "+340%",
    statLabel: "Bookings increase",
    tags: ["Logo Design", "Brand Identity", "Website"],
    testimonial:
      "BusiGrow completely reimagined our brand. The results speak for themselves.",
    clientName: "Aravind S.",
    country: "🇮🇳 India",
  },
  {
    title: "E-Commerce Growth Engine",
    category: "Marketing",
    client: "StyleBox Fashion",
    desc: "AI-powered marketing automation, paid advertising strategy, and conversion optimization. Consistent revenue growth.",
    image:
      "https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?auto=format&fit=crop&w=800&q=80",
    stat: "₹2.5Cr",
    statLabel: "Revenue in 6 months",
    tags: ["Paid Ads", "AI Automation", "SEO"],
    testimonial:
      "Our revenue tripled. The AI automation they built handles everything automatically.",
    clientName: "Priya M.",
    country: "🇮🇳 India",
  },
  {
    title: "FinTech Platform Launch",
    category: "Development",
    client: "PayWise Technologies",
    desc: "Full-stack web application with AI-powered analytics dashboard, payment integrations, and a mobile-responsive design that onboarded 100K users.",
    image:
      "https://images.unsplash.com/photo-1551288049-bebda4e38f71?auto=format&fit=crop&w=800&q=80",
    stat: "100K",
    statLabel: "Users in 3 months",
    tags: ["React", "Node.js", "AI Analytics"],
    testimonial:
      "They built exactly what we envisioned and delivered faster than expected.",
    clientName: "Rohan K.",
    country: "🇮🇳 India",
  },
  {
    title: "Real Estate AI Assistant",
    category: "AI Automation",
    client: "Gulf Properties LLC",
    desc: "Custom AI chatbot for WhatsApp that qualifies leads, schedules property viewings, answers FAQs in Arabic/English, and integrates with CRM.",
    image:
      "https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?auto=format&fit=crop&w=800&q=80",
    stat: "60%",
    statLabel: "Support cost reduction",
    tags: ["AI Chatbot", "WhatsApp", "CRM Integration"],
    testimonial:
      "The AI bot handles thousands of queries daily. Our team can finally focus on closing deals.",
    clientName: "Sara A.",
    country: "🇦🇪 UAE",
  },
  {
    title: "Healthcare App Redesign",
    category: "UI/UX Design",
    client: "MedConnect Clinics",
    desc: "Complete UI/UX overhaul of patient mobile app. Redesigned appointment booking, prescription management, and telemedicine.",
    image:
      "https://images.unsplash.com/photo-1576091160550-2173dba999ef?auto=format&fit=crop&w=800&q=80",
    stat: "2.5x",
    statLabel: "User engagement",
    tags: ["Mobile App", "UX Research", "Prototyping"],
    testimonial:
      "Patient satisfaction scores went through the roof after the redesign.",
    clientName: "Dr. Mehra",
    country: "🇮🇳 India",
  },
  {
    title: "Restaurant Chain Content",
    category: "Content",
    client: "Spice Route Dubai",
    desc: "End-to-end content strategy including food photography, video production, social media management, and influencer partnerships across 5 locations.",
    image:
      "https://images.unsplash.com/photo-1414235077428-338989a2e8c0?auto=format&fit=crop&w=800&q=80",
    stat: "8x",
    statLabel: "Social engagement",
    tags: ["Video", "Photography", "Social Media"],
    testimonial:
      "Our Instagram went from 2K to 45K followers in 4 months. The content is stunning.",
    clientName: "Chef Omar",
    country: "🇦🇪 UAE",
  },
  {
    title: "EdTech Platform & Marketing",
    category: "Development",
    client: "LearnNext Academy",
    desc: "Custom learning management system with live classes, assignment tracking, and parent portal. Full digital marketing launch.",
    image:
      "https://images.unsplash.com/photo-1522202176988-66273c2fd55f?auto=format&fit=crop&w=800&q=80",
    stat: "15K",
    statLabel: "Students enrolled",
    tags: ["LMS", "React", "Marketing"],
    testimonial:
      "From zero to 15,000 students. BusiGrow handled everything end to end.",
    clientName: "Vikram T.",
    country: "🇮🇳 India",
  },
  {
    title: "Fashion Brand Identity",
    category: "Branding",
    client: "Aura Couture",
    desc: "Premium fashion brand creation: name ideation, logo, packaging, lookbook, and e-commerce storefront.",
    image:
      "https://images.unsplash.com/photo-1445205170230-053b83016050?auto=format&fit=crop&w=800&q=80",
    stat: "₹80L",
    statLabel: "First year revenue",
    tags: ["Brand Identity", "Packaging", "E-commerce"],
    testimonial:
      "They didn't just design a logo. They built a brand people love.",
    clientName: "Ananya D.",
    country: "🇮🇳 India",
  },
  {
    title: "Logistics Automation",
    category: "AI Automation",
    client: "QuickShip Express",
    desc: "End-to-end workflow automation: order processing, route optimization, customer notifications, and GST invoices. Integrated with Tally ERP.",
    image:
      "https://images.unsplash.com/photo-1586528116311-ad8dd3c8310d?auto=format&fit=crop&w=800&q=80",
    stat: "70%",
    statLabel: "Time saved on operations",
    tags: ["Workflow", "Tally Integration", "WhatsApp"],
    testimonial:
      "What used to take 4 hours now takes 30 minutes. The ROI was immediate.",
    clientName: "Manoj P.",
    country: "🇮🇳 India",
  },
];

/* ─── HERO ─── */
const PortfolioHero = () => (
  <section className="relative pt-24 sm:pt-32 pb-16 sm:pb-20 overflow-hidden hero-mesh">
    <div className="absolute inset-0 overflow-hidden pointer-events-none">
      <div className="absolute top-1/3 left-1/4 w-96 h-96 bg-violet-300/20 rounded-full blur-[120px] animate-float-orb" />
      <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-purple-300/15 rounded-full blur-[120px] animate-float-orb-reverse" />
      <div className="absolute top-20 right-[15%] w-64 h-64 bg-gradient-to-br from-violet-200/25 to-purple-300/15 animate-morph-blob blur-xl" />
    </div>
    <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center">
        <motion.div initial="hidden" animate="visible" variants={stagger}>
          <motion.span
            variants={fadeUp}
            className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full glass text-sm text-slate-600"
          >
            <Layers className="w-4 h-4 text-violet-600" /> Our Portfolio
          </motion.span>
          <motion.h1
            variants={fadeUp}
            custom={1}
            className="mt-6 sm:mt-8 text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-slate-900 leading-[1.1]"
          >
            Real results for{" "}
            <span className="text-gradient-vibrant">real businesses</span>
          </motion.h1>
          <motion.p
            variants={fadeUp}
            custom={2}
            className="mt-4 sm:mt-6 text-base sm:text-lg text-slate-500 max-w-xl leading-relaxed"
          >
            Every project is a partnership. See how we've helped businesses
            across India, the Middle East, and Southeast Asia grow smarter and
            work faster.
          </motion.p>

          {/* Mini-stats */}
          <motion.div
            variants={fadeUp}
            custom={3}
            className="mt-8 sm:mt-10 flex flex-wrap gap-6 sm:gap-8"
          >
            {[
              { value: "150+", label: "Projects Delivered" },
              { value: "50+", label: "Happy Clients" },
              { value: "3", label: "Countries Served" },
            ].map((s, i) => (
              <div key={i}>
                <p className="text-2xl font-bold text-gradient">{s.value}</p>
                <p className="text-xs text-slate-400 mt-0.5">{s.label}</p>
              </div>
            ))}
          </motion.div>
        </motion.div>
        {/* Hero featured project image */}
        <motion.div
          initial={{ opacity: 0, x: 50, rotate: 1 }}
          animate={{ opacity: 1, x: 0, rotate: 0 }}
          transition={{ delay: 0.4, duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
          className="hidden lg:block"
        >
          <div className="relative">
            <div className="rounded-3xl overflow-hidden shadow-apple-xl animate-glow-pulse">
              <img
                src="https://images.unsplash.com/photo-1460925895917-afdab827c52f?auto=format&fit=crop&w=800&q=80"
                alt="Project analytics dashboard"
                className="w-full aspect-[4/3] object-cover"
              />
            </div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.2, duration: 0.5 }}
              className="absolute -bottom-6 -left-6 glass-strong rounded-2xl p-4 shadow-apple-lg animate-float-slow"
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center">
                  <TrendingUp className="w-5 h-5 text-white" />
                </div>
                <div>
                  <p className="text-xs text-slate-400">Avg. Growth</p>
                  <p className="text-lg font-bold text-slate-900">+247%</p>
                </div>
              </div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 1.5, type: "spring", stiffness: 200 }}
              className="absolute -top-4 -right-4 glass-strong rounded-2xl p-3 shadow-apple animate-float-slow"
              style={{ animationDelay: "-3s" }}
            >
              <div className="flex items-center gap-2">
                <div className="flex -space-x-1.5">
                  {["#7c3aed", "#6d28d9", "#a855f7"].map((c, i) => (
                    <div
                      key={i}
                      className="w-6 h-6 rounded-full border-2 border-white"
                      style={{ background: c }}
                    />
                  ))}
                </div>
                <span className="text-xs font-bold text-slate-700">
                  9 Projects
                </span>
              </div>
            </motion.div>
          </div>
        </motion.div>
      </div>
    </div>
  </section>
);

/* ─── FILTER + GRID ─── */
const PortfolioGrid = () => {
  const [activeFilter, setActiveFilter] = useState("All");
  const [selectedProject, setSelectedProject] = useState(null);

  const filtered =
    activeFilter === "All"
      ? projects
      : projects.filter((p) => p.category === activeFilter);

  return (
    <>
      <section className="py-12 sm:py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Filter pills — scrollable on mobile */}
          <div className="scroll-pills sm:flex sm:flex-wrap sm:gap-2 mb-8 sm:mb-12">
            {categories.map((cat) => (
              <button
                key={cat}
                onClick={() => setActiveFilter(cat)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-300 whitespace-nowrap flex-shrink-0 ${
                  activeFilter === cat
                    ? "bg-gradient-to-r from-violet-600 to-purple-600 text-white shadow-apple-lg"
                    : "glass text-slate-600 hover:text-violet-700 hover:bg-violet-50/60"
                }`}
              >
                {cat}
              </button>
            ))}
          </div>

          {/* Project grid */}
          <motion.div
            layout
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
          >
            <AnimatePresence mode="popLayout">
              {filtered.map((project) => (
                <motion.div
                  key={project.title}
                  layout
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.9 }}
                  transition={{ duration: 0.4 }}
                  onClick={() => setSelectedProject(project)}
                  className="group cursor-pointer glass card-glow-top card-shine rounded-2xl overflow-hidden shadow-apple hover:shadow-apple-xl transition-all duration-500"
                >
                  {/* Image */}
                  <div className="relative h-52 overflow-hidden">
                    <img
                      src={project.image}
                      alt={project.title}
                      className="w-full h-full object-cover img-parallax"
                      loading="lazy"
                      onError={handleImageFallback}
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-slate-900/60 via-transparent to-transparent" />
                    <div className="absolute bottom-3 right-3 glass-strong rounded-xl px-3 py-1.5 shadow-apple">
                      <p className="text-lg font-bold text-slate-900">
                        {project.stat}
                      </p>
                      <p className="text-[9px] text-slate-500 font-medium">
                        {project.statLabel}
                      </p>
                    </div>
                    <span className="absolute top-3 left-3 px-3 py-1 glass-strong rounded-full text-[11px] font-semibold text-slate-700">
                      {project.category}
                    </span>
                  </div>

                  {/* Content */}
                  <div className="p-5">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-xs text-slate-400">
                        {project.country}
                      </span>
                      <span className="text-xs text-slate-300">·</span>
                      <span className="text-xs text-slate-400">
                        {project.client}
                      </span>
                    </div>
                    <h3 className="text-base font-bold text-slate-900 group-hover:text-violet-700 transition-colors mb-1.5">
                      {project.title}
                    </h3>
                    <p className="text-sm text-slate-500 leading-relaxed line-clamp-2">
                      {project.desc}
                    </p>
                    <div className="mt-3 flex flex-wrap gap-1.5">
                      {project.tags.map((tag) => (
                        <span
                          key={tag}
                          className="px-2 py-0.5 rounded-md bg-violet-50 text-[10px] font-medium text-violet-600"
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>
                </motion.div>
              ))}
            </AnimatePresence>
          </motion.div>
        </div>
      </section>

      {/* Detail Modal */}
      <AnimatePresence>
        {selectedProject && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4"
            onClick={() => setSelectedProject(null)}
          >
            <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" />
            <motion.div
              initial={{ opacity: 0, y: 30, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 30, scale: 0.95 }}
              transition={{ duration: 0.3 }}
              onClick={(e) => e.stopPropagation()}
              className="relative w-full max-w-2xl sm:max-h-[90vh] max-h-[100dvh] overflow-y-auto bg-white sm:rounded-3xl rounded-none shadow-apple-xl"
            >
              <button
                onClick={() => setSelectedProject(null)}
                className="absolute top-4 right-4 z-10 p-2 rounded-full glass hover:bg-violet-50 transition-colors"
              >
                <X className="w-5 h-5 text-slate-600" />
              </button>

              <div className="relative h-48 sm:h-64 overflow-hidden sm:rounded-t-3xl">
                <img
                  src={selectedProject.image}
                  alt={selectedProject.title}
                  className="w-full h-full object-cover"
                  onError={handleImageFallback}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-white via-transparent to-transparent" />
              </div>

              <div className="p-5 sm:p-8 -mt-10 relative">
                <div className="flex items-center gap-3 mb-3">
                  <span className="px-3 py-1 rounded-full bg-violet-100 text-violet-700 text-xs font-semibold">
                    {selectedProject.category}
                  </span>
                  <span className="text-sm text-slate-400">
                    {selectedProject.country}
                  </span>
                </div>

                <h2 className="text-xl sm:text-2xl font-bold text-slate-900 mb-2">
                  {selectedProject.title}
                </h2>
                <p className="text-sm text-slate-400 mb-4">
                  Client: {selectedProject.client}
                </p>
                <p className="text-slate-600 leading-relaxed mb-6">
                  {selectedProject.desc}
                </p>

                {/* Result stat */}
                <div className="p-5 rounded-2xl bg-violet-50 border border-violet-100 mb-6">
                  <div className="flex items-center gap-4">
                    <TrendingUp className="w-8 h-8 text-violet-600" />
                    <div>
                      <p className="text-2xl sm:text-3xl font-bold text-gradient">
                        {selectedProject.stat}
                      </p>
                      <p className="text-sm text-slate-500">
                        {selectedProject.statLabel}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Tags */}
                <div className="flex flex-wrap gap-2 mb-6">
                  {selectedProject.tags.map((tag) => (
                    <span
                      key={tag}
                      className="px-3 py-1 rounded-lg bg-violet-50 text-xs font-semibold text-violet-600"
                    >
                      {tag}
                    </span>
                  ))}
                </div>

                {/* Testimonial */}
                {selectedProject.testimonial && (
                  <div className="p-5 rounded-2xl glass border border-violet-100">
                    <Quote className="w-5 h-5 text-violet-300 mb-2" />
                    <p className="text-sm text-slate-600 italic leading-relaxed mb-2">
                      "{selectedProject.testimonial}"
                    </p>
                    <p className="text-xs font-semibold text-slate-800">
                      {selectedProject.clientName}
                    </p>
                  </div>
                )}

                <Link
                  to="/contact"
                  className="mt-6 btn-shine inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-gradient-to-r from-violet-600 to-purple-600 text-white font-semibold hover:shadow-apple-lg transition-all"
                >
                  Start a Similar Project <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

/* ─── CTA ─── */
const CTASection = () => (
  <section className="py-16 sm:py-24">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="relative overflow-hidden rounded-2xl sm:rounded-[2rem] bg-gradient-to-br from-violet-600 via-purple-600 to-indigo-700 p-8 sm:p-12 md:p-20 text-center shadow-apple-xl">
        <div className="absolute -top-20 -left-20 w-40 sm:w-60 h-40 sm:h-60 rounded-full bg-white/10 blur-2xl" />
        <div className="absolute -bottom-20 -right-20 w-60 sm:w-80 h-60 sm:h-80 rounded-full bg-white/5 blur-3xl" />
        <div className="relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-2xl sm:text-3xl md:text-5xl font-bold text-white mb-4 sm:mb-5">
              Your success story is next.
            </h2>
            <p className="text-base sm:text-lg text-white/70 max-w-xl mx-auto mb-8 sm:mb-10">
              Join 50+ businesses that have grown faster and smarter with
              BusiGrow. Let's discuss your project today.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Link
                to="/contact"
                className="btn-shine group inline-flex items-center gap-3 px-8 py-4 bg-white text-violet-700 font-semibold rounded-2xl hover:bg-violet-50 hover:scale-[1.02] transition-all duration-300 shadow-apple-lg"
              >
                Start Your Project{" "}
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </Link>
              <Link
                to="/services"
                className="inline-flex items-center gap-2 px-6 py-4 border border-white/30 text-white font-semibold rounded-2xl hover:bg-white/10 transition-all duration-300"
              >
                Explore Services
              </Link>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  </section>
);

/* ─── PAGE ─── */
const TransformationsPage = () => (
  <div className="min-h-screen bg-[#FAFAFE] pb-24 md:pb-0">
    <Header />
    <PortfolioHero />
    <PortfolioGrid />
    <CTASection />
    <Footer />
  </div>
);

export default TransformationsPage;
